import { useEffect, useState } from 'react';
import AdminLayout from '../components/AdminLayout';
import { api } from '../../api';

export default function Languages() {
  const [langs, setLangs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);
  const [saving, setSaving] = useState(false);
  const [msg, setMsg] = useState('');
  const [search, setSearch] = useState('');

  const load = () => api.getLanguages().then(setLangs).catch(console.error).finally(() => setLoading(false));
  useEffect(() => { load(); }, []);

  const flash = (m) => { setMsg(m); setTimeout(() => setMsg(''), 3000); };

  const startEdit = (l) => setEditing({ ...l });

  const saveEdit = async () => {
    setSaving(true);
    try {
      await api.updateLanguage(editing.code, { label: editing.label, direction: editing.direction, audioTrack: editing.audioTrack !== '' ? parseInt(editing.audioTrack) : null });
      flash('Saved.');
      setEditing(null);
      load();
    } catch (err) {
      flash('Error: ' + err.message);
    } finally {
      setSaving(false);
    }
  };

  const toggle = async (code) => {
    try { await api.toggleLanguage(code); load(); } catch (err) { flash('Error: ' + err.message); }
  };

  const filtered = langs.filter((l) =>
    l.label.toLowerCase().includes(search.toLowerCase()) ||
    l.code.toLowerCase().includes(search.toLowerCase())
  );

  const active = langs.filter((l) => l.isActive).length;

  return (
    <AdminLayout>
      <div className="admin-page-header">
        <h1>Languages</h1>
        <p>Manage the 36 languages — enable/disable and set HLS audio track index</p>
      </div>

      {msg && (
        <div style={{ background: 'rgba(212,170,106,.15)', border: '1px solid var(--a-gold2)', borderRadius: 8, padding: '10px 16px', marginBottom: 20, fontSize: 14, color: 'var(--a-gold)' }}>
          {msg}
        </div>
      )}

      <div className="a-stats-grid" style={{ marginBottom: 20 }}>
        <div className="a-stat-card gold">
          <div className="a-stat-label">Total Languages</div>
          <div className="a-stat-value">{langs.length}</div>
        </div>
        <div className="a-stat-card green">
          <div className="a-stat-label">Active</div>
          <div className="a-stat-value">{active}</div>
        </div>
        <div className="a-stat-card">
          <div className="a-stat-label">Inactive</div>
          <div className="a-stat-value">{langs.length - active}</div>
        </div>
      </div>

      <div className="a-table-wrap">
        <div className="a-table-header">
          <h3>All Languages</h3>
          <input
            placeholder="Search…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            style={{ padding: '7px 12px', background: 'var(--a-surface2)', border: '1px solid var(--a-border)', borderRadius: 8, color: 'var(--a-text)', fontSize: 13, outline: 'none', width: 180 }}
          />
        </div>

        {loading ? (
          <div className="a-loader"><div className="a-spinner" /></div>
        ) : (
          <table className="a-table">
            <thead>
              <tr>
                <th>Code</th>
                <th>Label</th>
                <th>Dir</th>
                <th>Audio Track #</th>
                <th>Status</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((l) => (
                <tr key={l.code}>
                  <td><span className="a-badge blue">{l.code}</span></td>
                  <td>
                    {editing?.code === l.code ? (
                      <input
                        value={editing.label}
                        onChange={(e) => setEditing({ ...editing, label: e.target.value })}
                        style={{ padding: '5px 8px', background: 'var(--a-surface2)', border: '1px solid var(--a-gold2)', borderRadius: 6, color: 'var(--a-text)', fontSize: 14, width: 150 }}
                      />
                    ) : (
                      <span style={{ fontWeight: 600 }}>{l.label}</span>
                    )}
                  </td>
                  <td>
                    {editing?.code === l.code ? (
                      <select
                        value={editing.direction}
                        onChange={(e) => setEditing({ ...editing, direction: e.target.value })}
                        style={{ padding: '5px 8px', background: 'var(--a-surface2)', border: '1px solid var(--a-border)', borderRadius: 6, color: 'var(--a-text)', fontSize: 13 }}
                      >
                        <option value="ltr">ltr</option>
                        <option value="rtl">rtl</option>
                      </select>
                    ) : (
                      <span className={`a-badge ${l.direction === 'rtl' ? 'gold' : ''}`}>{l.direction}</span>
                    )}
                  </td>
                  <td>
                    {editing?.code === l.code ? (
                      <input
                        type="number"
                        min="0"
                        value={editing.audioTrack ?? ''}
                        onChange={(e) => setEditing({ ...editing, audioTrack: e.target.value })}
                        placeholder="0"
                        style={{ padding: '5px 8px', background: 'var(--a-surface2)', border: '1px solid var(--a-border)', borderRadius: 6, color: 'var(--a-text)', fontSize: 14, width: 70 }}
                      />
                    ) : (
                      <span style={{ color: l.audioTrack != null ? 'var(--a-text)' : 'var(--a-muted)' }}>
                        {l.audioTrack != null ? `Track ${l.audioTrack}` : '—'}
                      </span>
                    )}
                  </td>
                  <td>
                    <span className={`a-badge ${l.isActive ? 'green' : 'red'}`}>
                      {l.isActive ? 'Active' : 'Inactive'}
                    </span>
                  </td>
                  <td>
                    <div className="a-btn-row">
                      {editing?.code === l.code ? (
                        <>
                          <button className="a-icon-btn success" onClick={saveEdit} disabled={saving}>
                            <i className="fas fa-check" /> {saving ? '…' : 'Save'}
                          </button>
                          <button className="a-icon-btn" onClick={() => setEditing(null)}>Cancel</button>
                        </>
                      ) : (
                        <>
                          <button className="a-icon-btn" onClick={() => startEdit(l)}>
                            <i className="fas fa-edit" /> Edit
                          </button>
                          <label className="a-toggle" title={l.isActive ? 'Disable' : 'Enable'}>
                            <input type="checkbox" checked={l.isActive} onChange={() => toggle(l.code)} />
                            <span className="a-toggle-slider" />
                          </label>
                        </>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </AdminLayout>
  );
}
